// src/components/SmartFlow/NodeConfigModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../services/api";
import JSONTreeView from "./JSONTreeView";
import { getNodeConfigComponent, hasCustomConfigComponent } from "../nodeConfigRegistry";
import "./NodeConfigModal.css";

const clamp = (n, min, max) => Math.min(max, Math.max(min, n));

/**
 * Node Configuration Modal - Generic Orchestrator
 * - Layout com 3 painéis: Input | Parameters | Output
 * - Painéis redimensionáveis (split panes)
 * - Delegação de UI customizada via registry
 */
const NodeConfigModal = ({ node, inputData, outputData, onSave, onClose, onRunNode }) => {
  const [config, setConfig] = useState(node.data.config || {});
  const [nodeMetadata, setNodeMetadata] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [activeTab, setActiveTab] = useState("parameters");
  const [running, setRunning] = useState(false);
  const [localOutputData, setLocalOutputData] = useState(outputData);

  // Refs para campos (drag & drop de variáveis)
  const fieldRefs = useRef({});
  const isMountedRef = useRef(true);

  // ===== Split panes (n8n-like) =====
  const LS_KEY = "smartflow.nodeConfigModal.panes";
  const defaultPanes = useMemo(() => ({ left: 320, right: 360 }), []);
  const [paneSizes, setPaneSizes] = useState(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return {
        left: typeof parsed?.left === "number" ? parsed.left : defaultPanes.left,
        right: typeof parsed?.right === "number" ? parsed.right : defaultPanes.right,
      };
    } catch {
      return defaultPanes;
    }
  });

  const dragRef = useRef({
    active: false,
    which: null, // "left" | "right"
    startX: 0,
    startLeft: 0,
    startRight: 0,
  });

  useEffect(() => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(paneSizes));
    } catch {}
  }, [paneSizes]);

  const resetPanes = () => setPaneSizes(defaultPanes);

  const beginPaneResize = (which, e) => {
    e.preventDefault();
    e.stopPropagation();

    dragRef.current = {
      active: true,
      which,
      startX: e.clientX,
      startLeft: paneSizes.left,
      startRight: paneSizes.right,
    };

    document.documentElement.classList.add("sf-nodeConfigModal--resizing");
  };

  useEffect(() => {
    const onMove = (e) => {
      if (!dragRef.current.active) return;

      const dx = e.clientX - dragRef.current.startX;

      const MIN_LEFT = 240;
      const MAX_LEFT = 720;
      const MIN_RIGHT = 260;
      const MAX_RIGHT = 820;

      if (dragRef.current.which === "left") {
        const nextLeft = clamp(dragRef.current.startLeft + dx, MIN_LEFT, MAX_LEFT);
        setPaneSizes((prev) => ({ ...prev, left: nextLeft }));
      }

      if (dragRef.current.which === "right") {
        const nextRight = clamp(dragRef.current.startRight - dx, MIN_RIGHT, MAX_RIGHT);
        setPaneSizes((prev) => ({ ...prev, right: nextRight }));
      }
    };

    const onUp = () => {
      if (!dragRef.current.active) return;
      dragRef.current.active = false;
      document.documentElement.classList.remove("sf-nodeConfigModal--resizing");
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [paneSizes.left, paneSizes.right]);

  useEffect(() => {
    isMountedRef.current = true;
    setConfig(node.data.config || {});
    fetchNodeMetadata();

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node]);

  useEffect(() => {
    if (isMountedRef.current) setLocalOutputData(outputData);
  }, [outputData]);

  const fetchNodeMetadata = async () => {
    try {
      if (!isMountedRef.current) return;

      setLoading(true);
      setError(null);

      const response = await api.get(`/api/workflows/nodes/${node.type}`);
      if (isMountedRef.current) setNodeMetadata(response.data);
    } catch (err) {
      console.error("Error fetching node metadata:", err);
      if (isMountedRef.current) setError("Failed to load node configuration schema");
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  const validateConfig = () => {
    if (!nodeMetadata?.config_schema) return true;

    const errors = {};
    const schema = nodeMetadata.config_schema;

    Object.keys(schema).forEach((fieldName) => {
      const fieldSchema = schema[fieldName];

      if (fieldSchema.required && !config[fieldName]) {
        errors[fieldName] = `${fieldSchema.description || fieldName} is required`;
      }

      if (config[fieldName] !== undefined && config[fieldName] !== null) {
        const value = config[fieldName];
        const type = fieldSchema.type;

        if (type === "integer" && !Number.isInteger(Number(value))) errors[fieldName] = "Must be a valid integer";
        if (type === "boolean" && typeof value !== "boolean") errors[fieldName] = "Must be true or false";
        if (type === "array" && !Array.isArray(value)) errors[fieldName] = "Must be an array";
        if (type === "object" && typeof value !== "object") errors[fieldName] = "Must be an object";
        if (fieldSchema.enum && !fieldSchema.enum.includes(value)) {
          errors[fieldName] = `Must be one of: ${fieldSchema.enum.join(", ")}`;
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (validateConfig()) onSave(config);
  };

  const handleConfigChange = (newConfig) => {
    setConfig(newConfig);

    const changedFields = Object.keys(newConfig).filter((key) => newConfig[key] !== config[key]);
    if (changedFields.length) {
      setValidationErrors((prev) => {
        const next = { ...prev };
        changedFields.forEach((field) => delete next[field]);
        return next;
      });
    }
  };

  const handleRunNode = async () => {
    if (!onRunNode) {
      alert("Run node function not provided");
      return;
    }
    if (!isMountedRef.current) return;

    setRunning(true);
    try {
      const result = await onRunNode(node.id, config, inputData);
      if (isMountedRef.current) setLocalOutputData(result);
    } catch (err) {
      console.error("Error running node:", err);
      if (isMountedRef.current) setLocalOutputData({ error: err.message || "Failed to execute node" });
    } finally {
      if (isMountedRef.current) setRunning(false);
    }
  };

  const handlePropertyDragStart = (e, path) => {
    e.dataTransfer.setData("text/plain", `{{ ${path} }}`);
    e.dataTransfer.effectAllowed = "copy";
  };

  const renderParametersTab = () => {
    if (loading) {
      return (
        <div className="sf-nodeConfigModal__state">
          <div className="sf-nodeConfigModal__stateTitle">Loading configuration...</div>
          <div className="sf-nodeConfigModal__stateHint">Fetching node schema from API</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="sf-nodeConfigModal__state">
          <div className="sf-nodeConfigModal__errorText">{error}</div>
          <button className="sf-nodeConfigModal__primaryButton" onClick={fetchNodeMetadata}>
            Retry
          </button>
        </div>
      );
    }

    if (!nodeMetadata?.config_schema) {
      return <div className="sf-nodeConfigModal__mutedCentered">This node has no configuration options.</div>;
    }

    const CustomConfigComponent = getNodeConfigComponent(node.type);
    if (CustomConfigComponent) {
      return (
        <CustomConfigComponent
          config={config}
          nodeMetadata={nodeMetadata}
          onChange={handleConfigChange}
          validationErrors={validationErrors}
          inputData={inputData}
        />
      );
    }

    return renderGenericFields();
  };

  const renderGenericFields = () => {
    const schema = nodeMetadata.config_schema;

    return (
      <div className="sf-nodeConfigModal__form">
        {nodeMetadata.description && (
          <div className="sf-nodeConfigModal__descriptionCallout">{nodeMetadata.description}</div>
        )}

        {Object.keys(schema).map((fieldName) => renderGenericField(fieldName, schema[fieldName]))}

        {Object.keys(validationErrors).length > 0 && (
          <div className="sf-nodeConfigModal__validationBox">
            <strong>Please fix the following errors:</strong>
            <ul className="sf-nodeConfigModal__validationList">
              {Object.values(validationErrors).map((msg, idx) => (
                <li key={idx}>{msg}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderGenericField = (fieldName, fieldSchema) => {
    const value = config[fieldName] ?? fieldSchema.default ?? "";
    const errorMsg = validationErrors[fieldName];

    const setFieldValue = (nextValue) => handleConfigChange({ ...config, [fieldName]: nextValue });

    const labelText = fieldSchema.description || fieldName;
    const requiredMark = fieldSchema.required ? <span className="sf-nodeConfigModal__requiredMark"> *</span> : null;

    // select
    if (fieldSchema.type === "string" && fieldSchema.enum) {
      return (
        <label key={fieldName} className="sf-nodeConfigModal__field">
          <div className="sf-nodeConfigModal__fieldLabel">
            <strong>{labelText}</strong>
            {requiredMark}
          </div>

          <select
            className={`sf-nodeConfigModal__control ${errorMsg ? "sf-nodeConfigModal__control--invalid" : ""}`}
            value={value}
            onChange={(e) => setFieldValue(e.target.value)}
          >
            <option value="">Select {fieldName}</option>
            {fieldSchema.enum.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>

          {errorMsg && <div className="sf-nodeConfigModal__fieldError">{errorMsg}</div>}
        </label>
      );
    }

    // textarea string
    if (fieldSchema.type === "string" && fieldSchema.multiline) {
      return (
        <label key={fieldName} className="sf-nodeConfigModal__field">
          <div className="sf-nodeConfigModal__fieldLabel">
            <strong>{labelText}</strong>
            {requiredMark}
          </div>

          <textarea
            className={`sf-nodeConfigModal__control ${errorMsg ? "sf-nodeConfigModal__control--invalid" : ""}`}
            value={value}
            onChange={(e) => setFieldValue(e.target.value)}
            placeholder={fieldSchema.placeholder || `Enter ${fieldName}`}
            rows={4}
          />

          {errorMsg && <div className="sf-nodeConfigModal__fieldError">{errorMsg}</div>}
        </label>
      );
    }

    // text string
    if (fieldSchema.type === "string") {
      return (
        <label key={fieldName} className="sf-nodeConfigModal__field">
          <div className="sf-nodeConfigModal__fieldLabel">
            <strong>{labelText}</strong>
            {requiredMark}
          </div>

          <input
            className={`sf-nodeConfigModal__control ${errorMsg ? "sf-nodeConfigModal__control--invalid" : ""}`}
            type="text"
            value={value}
            onChange={(e) => setFieldValue(e.target.value)}
            placeholder={fieldSchema.placeholder || `Enter ${fieldName}`}
          />

          {errorMsg && <div className="sf-nodeConfigModal__fieldError">{errorMsg}</div>}
        </label>
      );
    }

    // number/integer
    if (fieldSchema.type === "integer" || fieldSchema.type === "number") {
      return (
        <label key={fieldName} className="sf-nodeConfigModal__field">
          <div className="sf-nodeConfigModal__fieldLabel">
            <strong>{labelText}</strong>
            {requiredMark}
          </div>

          <input
            className={`sf-nodeConfigModal__control ${errorMsg ? "sf-nodeConfigModal__control--invalid" : ""}`}
            type="number"
            value={value}
            min={fieldSchema.min}
            max={fieldSchema.max}
            step={fieldSchema.type === "integer" ? 1 : "any"}
            onChange={(e) => {
              const raw = e.target.value;
              const parsed =
                fieldSchema.type === "integer"
                  ? parseInt(raw, 10)
                  : parseFloat(raw);
              setFieldValue(Number.isFinite(parsed) ? parsed : 0);
            }}
            placeholder={fieldSchema.placeholder || `Enter ${fieldName}`}
          />

          {errorMsg && <div className="sf-nodeConfigModal__fieldError">{errorMsg}</div>}
        </label>
      );
    }

    // boolean
    if (fieldSchema.type === "boolean") {
      return (
        <label key={fieldName} className="sf-nodeConfigModal__checkboxField">
          <input
            className="sf-nodeConfigModal__checkbox"
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => setFieldValue(e.target.checked)}
          />
          <div className="sf-nodeConfigModal__checkboxLabel">
            <strong>{labelText}</strong>
            {requiredMark}
            {errorMsg && <span className="sf-nodeConfigModal__inlineError"> {errorMsg}</span>}
          </div>
        </label>
      );
    }

    // array/object JSON textarea
    if (fieldSchema.type === "array" || fieldSchema.type === "object") {
      const jsonText = typeof value === "string" ? value : JSON.stringify(value, null, 2);

      return (
        <label key={fieldName} className="sf-nodeConfigModal__field">
          <div className="sf-nodeConfigModal__fieldLabel">
            <strong>{labelText}</strong>
            {requiredMark}
          </div>

          <textarea
            className={`sf-nodeConfigModal__control sf-nodeConfigModal__control--mono ${
              errorMsg ? "sf-nodeConfigModal__control--invalid" : ""
            }`}
            value={jsonText}
            onChange={(e) => {
              const nextText = e.target.value;
              try {
                setFieldValue(JSON.parse(nextText));
              } catch {
                // mantém texto enquanto está inválido (para não travar digitação)
                setFieldValue(nextText);
              }
            }}
            placeholder={fieldSchema.placeholder || (fieldSchema.type === "array" ? `["item1"]` : `{"key":"value"}`)}
            rows={fieldSchema.type === "array" ? 3 : 4}
          />

          {errorMsg && <div className="sf-nodeConfigModal__fieldError">{errorMsg}</div>}
        </label>
      );
    }

    return null;
  };

  const modalTitle = nodeMetadata?.display_name || node.type.replace(/_/g, " ");

  return (
    <div className="sf-nodeConfigModal__overlay" onClick={onClose} key={`config-modal-${node.id}`}>
      <div className="sf-nodeConfigModal__shell" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sf-nodeConfigModal__header">
          <div className="sf-nodeConfigModal__headerInfo">
            <h2 className="sf-nodeConfigModal__title">{modalTitle}</h2>

            {nodeMetadata?.category && (
              <div className="sf-nodeConfigModal__subtitle">
                Category: {nodeMetadata.category}
                {hasCustomConfigComponent(node.type) && (
                  <span className="sf-nodeConfigModal__customBadge">✓ Custom UI</span>
                )}
              </div>
            )}
          </div>

          <div className="sf-nodeConfigModal__headerActions">
            <button
              className={`sf-nodeConfigModal__runButton ${running ? "sf-nodeConfigModal__runButton--busy" : ""}`}
              onClick={handleRunNode}
              disabled={running || loading}
              type="button"
            >
              {running ? "⏳ Running..." : "▶ Run Node"}
            </button>

            <button className="sf-nodeConfigModal__closeButton" onClick={onClose} type="button" aria-label="Close">
              ×
            </button>
          </div>
        </div>

        {/* Resizable layout */}
        <div
          className="sf-nodeConfigModal__workspace"
          style={{
            gridTemplateColumns: `${paneSizes.left}px 10px 1fr 10px ${paneSizes.right}px`,
          }}
        >
          {/* Input panel */}
          <section className="sf-nodeConfigModal__panel sf-nodeConfigModal__panel--input" aria-label="Input panel">
            <header className="sf-nodeConfigModal__panelHeader">
              <span className="sf-nodeConfigModal__panelTitle">📥 INPUT</span>
            </header>

            <div className="sf-nodeConfigModal__panelBody">
              {inputData ? (
                <JSONTreeView data={inputData} path="$input" onPropertyDragStart={handlePropertyDragStart} />
              ) : (
                <div className="sf-nodeConfigModal__emptyState">
                  No input data available.
                  <br />
                  <br />
                  Execute the workflow or previous nodes to see input data here.
                </div>
              )}
            </div>
          </section>

          {/* Splitter (Input | Center) */}
          <div
            className="sf-nodeConfigModal__splitter"
            onMouseDown={(e) => beginPaneResize("left", e)}
            onDoubleClick={resetPanes}
            title="Drag to resize • Double-click to reset"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize input panel"
          >
            <div className="sf-nodeConfigModal__splitterGrip" />
          </div>

          {/* Center panel */}
          <section className="sf-nodeConfigModal__panel sf-nodeConfigModal__panel--center" aria-label="Parameters panel">
            <nav className="sf-nodeConfigModal__tabs" aria-label="Node configuration tabs">
              <button
                className={`sf-nodeConfigModal__tab ${activeTab === "parameters" ? "sf-nodeConfigModal__tab--active" : ""}`}
                onClick={() => setActiveTab("parameters")}
                type="button"
              >
                Parameters
              </button>

              <button
                className={`sf-nodeConfigModal__tab ${activeTab === "settings" ? "sf-nodeConfigModal__tab--active" : ""}`}
                onClick={() => setActiveTab("settings")}
                type="button"
              >
                Settings
              </button>
            </nav>

            <div className="sf-nodeConfigModal__centerContent">
              {activeTab === "parameters" && renderParametersTab()}

              {activeTab === "settings" && (
                <div className="sf-nodeConfigModal__mutedText">
                  <p>Node settings (optional metadata, retries, etc.) can be added here in the future.</p>
                </div>
              )}
            </div>
          </section>

          {/* Splitter (Center | Output) */}
          <div
            className="sf-nodeConfigModal__splitter"
            onMouseDown={(e) => beginPaneResize("right", e)}
            onDoubleClick={resetPanes}
            title="Drag to resize • Double-click to reset"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize output panel"
          >
            <div className="sf-nodeConfigModal__splitterGrip" />
          </div>

          {/* Output panel */}
          <section className="sf-nodeConfigModal__panel sf-nodeConfigModal__panel--output" aria-label="Output panel">
            <header className="sf-nodeConfigModal__panelHeader">
              <span className="sf-nodeConfigModal__panelTitle">📤 OUTPUT</span>
            </header>

            <div className="sf-nodeConfigModal__panelBody">
              {localOutputData ? (
                localOutputData.error ? (
                  <div className="sf-nodeConfigModal__errorBox">
                    <strong>Error:</strong>
                    <div className="sf-nodeConfigModal__errorBoxMessage">{localOutputData.error}</div>
                  </div>
                ) : (
                  <JSONTreeView data={localOutputData} path="$output" onPropertyDragStart={handlePropertyDragStart} />
                )
              ) : (
                <div className="sf-nodeConfigModal__emptyState">
                  This node has not been executed yet.
                  <br />
                  <br />
                  Click "Run Node" to see the output here.
                </div>
              )}
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="sf-nodeConfigModal__footer">
          <div className="sf-nodeConfigModal__footerHint">
            {nodeMetadata?.version && <span>v{nodeMetadata.version}</span>}
            <span className="sf-nodeConfigModal__footerDivider">•</span>
            <span>💡 Drag properties from INPUT/OUTPUT to parameter fields</span>
          </div>

          <div className="sf-nodeConfigModal__footerActions">
            <button className="sf-nodeConfigModal__secondaryButton" onClick={onClose} type="button">
              Cancel
            </button>
            <button
              className="sf-nodeConfigModal__primaryButton"
              onClick={handleSave}
              disabled={loading}
              type="button"
            >
              Save Configuration
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NodeConfigModal;
